/** @type {ReadonlyArray<string>} */
export const DATE_PRESET_VALUES = Object.freeze(['thisMonth', 'lastMonth', 'thisYear', 'from', 'to', 'between']);

/**
 * @param {string} value
 * @returns {{ preset: string, date: string | null, dateEnd: string | null }}
 */
export function parseDateClause(value) {
	if (value === 'thisMonth' || value === 'lastMonth' || value === 'thisYear') { return { preset: value, date: null, dateEnd: null }; }
	if (value.startsWith('from:')) { return { preset: 'from', date: value.slice(5), dateEnd: null }; }
	if (value.startsWith('to:')) { return { preset: 'to', date: value.slice(3), dateEnd: null }; }
	if (value.startsWith('between:')) {
		const rest = value.slice(8);
		const parts = rest.split(':');
		if (parts.length === 2) { return { preset: 'between', date: parts[0] ?? null, dateEnd: parts[1] ?? null }; }
	}
	return { preset: '', date: null, dateEnd: null };
}

/**
 * @param {string} preset
 * @param {string} [date]
 * @param {string} [dateEnd]
 * @returns {string}
 */
export function buildDateClause(preset, date, dateEnd) {
	if (preset === 'from' || preset === 'to') { return `${preset}:${date ?? ''}`; }
	if (preset === 'between') { return `between:${date ?? ''}:${dateEnd ?? ''}`; }
	return preset;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isDateClause(value) {
	if (value === 'thisMonth' || value === 'lastMonth' || value === 'thisYear') { return true; }
	if (/^from:\d{4}-\d{2}-\d{2}$/.test(value)) { return true; }
	if (/^to:\d{4}-\d{2}-\d{2}$/.test(value)) { return true; }
	if (/^between:\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/.test(value)) { return true; }
	return false;
}

/**
 * @param {string} preset
 * @param {Date} [now]
 * @returns {{ start: string, end: string } | null}
 */
export function getDatePresetBounds(preset, now = new Date()) {
	const y = now.getFullYear();
	const m = now.getMonth();
	if (preset === 'thisMonth') {
		const start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
		const last = new Date(y, m + 1, 0).getDate();
		const end = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
		return { start, end };
	}
	if (preset === 'lastMonth') {
		const d = new Date(y, m - 1, 1);
		const ly = d.getFullYear();
		const lm = d.getMonth();
		const start = `${ly}-${String(lm + 1).padStart(2, '0')}-01`;
		const last = new Date(ly, lm + 1, 0).getDate();
		const end = `${ly}-${String(lm + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
		return { start, end };
	}
	if (preset === 'thisYear') { return { start: `${y}-01-01`, end: `${y}-12-31` }; }
	return null;
}

/**
 * @param {string} haystack
 * @param {string} clauseValue
 * @param {Date} [now]
 * @returns {boolean}
 */
export function matchesDateClause(haystack, clauseValue, now = new Date()) {
	const iso = String(haystack ?? '').slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return false; }
	if (clauseValue === 'thisMonth' || clauseValue === 'lastMonth' || clauseValue === 'thisYear') {
		const b = getDatePresetBounds(clauseValue, now);
		if (!b) { return false; }
		return iso >= b.start && iso <= b.end;
	}
	if (clauseValue.startsWith('from:')) {
		const d = clauseValue.slice(5);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) { return false; }
		return iso >= d;
	}
	if (clauseValue.startsWith('to:')) {
		const d = clauseValue.slice(3);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) { return false; }
		return iso <= d;
	}
	if (clauseValue.startsWith('between:')) {
		const rest = clauseValue.slice(8);
		const parts = rest.split(':');
		if (parts.length !== 2) { return false; }
		const s = parts[0]; const e = parts[1];
		if (!s || !e || !/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e)) { return false; }
		return iso >= s && iso <= e;
	}
	return false;
}
